import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TourCard from '../../components/base/TourCard';
import PriceRangeSlider from '../../components/base/PriceRangeSlider';
import { allTours, Tour } from '../../mocks/tours';

type SortKey = 'default' | 'price-asc' | 'price-desc' | 'name-asc';

const CATEGORIES = [
  { value: 'all', label: 'Tüm Turlar', icon: 'ri-compass-3-line' },
  { value: 'gunubirlik', label: 'Günübirlik', icon: 'ri-sun-line' },
  { value: 'konaklamali', label: 'Konaklamalı', icon: 'ri-hotel-bed-line' },
  { value: 'ulasimli', label: 'Ulaşımlı Tatil', icon: 'ri-bus-line' },
  { value: 'yurtdisi', label: 'Yurt Dışı', icon: 'ri-flight-takeoff-line' },
];

const DURATION_OPTIONS = [
  { label: 'Tümü', min: 0, max: 99 },
  { label: '1 Gün', min: 1, max: 1 },
  { label: '2-3 Gün', min: 2, max: 3 },
  { label: '4-5 Gün', min: 4, max: 5 },
  { label: '6+ Gün', min: 6, max: 99 },
];

const TL_TOURS_MIN = 1990;
const TL_TOURS_MAX = 30000;

export default function AraPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([TL_TOURS_MIN, TL_TOURS_MAX]);
  const [durationIdx, setDurationIdx] = useState(0);
  const [sort, setSort] = useState<SortKey>('default');
  const [showFilters, setShowFilters] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const tlTours = useMemo(() => allTours.filter(t => t.currency === 'TL'), []);
  const eurTours = useMemo(() => allTours.filter(t => t.currency === '€'), []);

  const filtered = useMemo(() => {
    let result: Tour[] = [];

    if (category === 'all' || category === 'gunubirlik') result.push(...tlTours.filter(t => t.category === 'gunubirlik'));
    if (category === 'all' || category === 'konaklamali') result.push(...tlTours.filter(t => t.category === 'konaklamali'));
    if (category === 'all' || category === 'ulasimli') result.push(...tlTours.filter(t => t.category === 'ulasimli'));
    if (category === 'all' || category === 'yurtdisi') result.push(...eurTours);

    if (search.trim()) {
      result = result.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
    }

    const dur = DURATION_OPTIONS[durationIdx];
    if (dur.min > 0) {
      result = result.filter(t => t.duration >= dur.min && t.duration <= dur.max);
    }

    // Price filter only for TL tours when not showing yurtdisi only
    if (category !== 'yurtdisi') {
      result = result.filter(t => {
        if (t.currency === '€') return true;
        return t.price >= priceRange[0] && t.price <= priceRange[1];
      });
    }

    switch (sort) {
      case 'price-asc': return [...result].sort((a, b) => a.price - b.price);
      case 'price-desc': return [...result].sort((a, b) => b.price - a.price);
      case 'name-asc': return [...result].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
      default: return result;
    }
  }, [search, category, priceRange, durationIdx, sort, tlTours, eurTours]);

  const resetFilters = () => {
    setSearch('');
    setCategory('all');
    setPriceRange([TL_TOURS_MIN, TL_TOURS_MAX]);
    setDurationIdx(0);
    setSort('default');
  };

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/ara`,
    url: `${siteUrl}/ara`,
    name: 'Tur Arama | Ankara Travelers',
    description: 'Ankara çıkışlı tüm tur paketlerinde arama yapın. Günübirlik, konaklamalı, ulaşımlı tatil ve yurt dışı turları filtreleyin.',
    inLanguage: 'tr-TR',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/ara?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
    publisher: {
      '@type': 'TravelAgency',
      name: 'Ankara Travelers',
      url: siteUrl,
    },
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <title>Tur Arama | Ankara Travelers - Ankara Çıkışlı Tüm Turlar</title>
      <meta name="description" content="Ankara çıkışlı tüm tur paketlerinde arama ve filtreleme yapın. Günübirlik turlar, konaklamalı turlar, ulaşımlı tatil paketleri ve yurt dışı turlar arasından en uygununu bulun." />
      <meta name="keywords" content="Ankara tur arama, Ankara çıkışlı tur bul, tur filtrele, günübirlik tur ara, yurt dışı tur ara" />
      <link rel="canonical" href={`${siteUrl}/ara`} />
      <meta property="og:title" content="Tur Arama | Ankara Travelers" />
      <meta property="og:description" content="Ankara çıkışlı tüm tur paketlerinde arama yapın. Binlerce tur seçeneği arasından filtreleyerek hayalinizdeki turu bulun." />
      <meta property="og:url" content={`${siteUrl}/ara`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      <Navbar />

      {/* Hero Search */}
      <section className="pt-20 bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl font-bold text-white mb-3">Tur Arama</h1>
          <p className="text-orange-100 mb-8">Binlerce tur arasında hayalinizdekini bulun</p>
          <div className="relative">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-lg" />
            </div>
            <input
              type="text"
              placeholder="Destinasyon veya tur adı arayın..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-12 pr-5 py-4 rounded-2xl text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>

          {/* Category Chips */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  category === c.value
                    ? 'bg-white text-orange-600 shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={c.icon} />
                </div>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 dark:text-white text-sm">Filtreler</h3>
                <button
                  onClick={resetFilters}
                  className="text-xs text-orange-500 hover:text-orange-700 cursor-pointer"
                >
                  Sıfırla
                </button>
              </div>

              {/* Price Range - only for TL tours */}
              {category !== 'yurtdisi' && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-money-turkish-lira-circle-line text-orange-500" />
                      </div>
                      Fiyat Aralığı (TL)
                    </span>
                  </h4>
                  <PriceRangeSlider
                    min={TL_TOURS_MIN}
                    max={TL_TOURS_MAX}
                    step={500}
                    value={priceRange}
                    onChange={setPriceRange}
                    formatLabel={v => `${v.toLocaleString('tr-TR')} ₺`}
                  />
                </div>
              )}

              {/* Duration */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-orange-500" />
                  </div>
                  Süre
                </h4>
                <div className="flex flex-col gap-2">
                  {DURATION_OPTIONS.map((d, i) => (
                    <button
                      key={i}
                      onClick={() => setDurationIdx(i)}
                      className={`text-left px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                        durationIdx === i
                          ? 'bg-orange-500 text-white font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:text-orange-600'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-sort-asc text-orange-500" />
                  </div>
                  Sırala
                </h4>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value as SortKey)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white text-gray-700 focus:outline-none focus:border-orange-400 cursor-pointer"
                >
                  <option value="default">Varsayılan</option>
                  <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                  <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                  <option value="name-asc">İsim: A-Z</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {/* Mobile filter toggle */}
            <div className="lg:hidden flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">{filtered.length} tur bulundu</span>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line" />
                </div>
                Filtrele
              </button>
            </div>

            {/* Mobile filter panel */}
            {showFilters && (
              <div className="lg:hidden mb-6 bg-gray-50 rounded-xl p-4 space-y-5">
                {category !== 'yurtdisi' && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Fiyat Aralığı (TL)</h4>
                    <PriceRangeSlider
                      min={TL_TOURS_MIN}
                      max={TL_TOURS_MAX}
                      step={500}
                      value={priceRange}
                      onChange={setPriceRange}
                      formatLabel={v => `${v.toLocaleString('tr-TR')} ₺`}
                    />
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Süre</h4>
                  <div className="flex flex-wrap gap-2">
                    {DURATION_OPTIONS.map((d, i) => (
                      <button
                        key={i}
                        onClick={() => setDurationIdx(i)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer ${
                          durationIdx === i ? 'bg-orange-500 text-white' : 'bg-white border border-gray-200 text-gray-600'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Sırala</h4>
                  <select
                    value={sort}
                    onChange={e => setSort(e.target.value as SortKey)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer"
                  >
                    <option value="default">Varsayılan</option>
                    <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                    <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                    <option value="name-asc">İsim: A-Z</option>
                  </select>
                </div>
              </div>
            )}

            {/* Desktop results count + sort bar */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-800 dark:text-white">{filtered.length}</span> tur bulundu
              </span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">Sırala:</span>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value as SortKey)}
                  className="px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white text-gray-700 focus:outline-none focus:border-orange-400 cursor-pointer"
                >
                  <option value="default">Varsayılan</option>
                  <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                  <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                  <option value="name-asc">İsim: A-Z</option>
                </select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-6xl text-gray-200" />
                </div>
                <p className="text-lg font-medium text-gray-500">Sonuç bulunamadı</p>
                <p className="text-sm mt-1">Farklı filtre seçenekleri deneyin</p>
                <button
                  onClick={resetFilters}
                  className="mt-4 px-5 py-2 bg-orange-500 text-white rounded-full text-sm font-medium hover:bg-orange-600 transition-colors cursor-pointer"
                >
                  Filtreleri Temizle
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map(tour => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <section className="bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 py-10 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4">Hızlı Bağlantılar</h3>
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.slice(1).map(c => (
              <Link
                key={c.value}
                to={`/${c.value === 'gunubirlik' ? 'gunubirlik-turlar' : c.value === 'konaklamali' ? 'konaklamali-turlar' : c.value === 'ulasimli' ? 'ulasimli-tatil' : 'yurtdisi-turlar'}`}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-sm text-gray-600 dark:text-gray-300 hover:border-orange-300 hover:text-orange-500 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={c.icon} />
                </div>
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
